import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import WalletRoundedIcon from "@mui/icons-material/WalletRounded";
import { useNavigate } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { ownerFinanceStatsApi } from "../../../api/ownerFinanceStats";
import { formatApiError } from "../../../api/client";
import { dateTimeLocalValue, idOf, money, shortDate, surfaceSx } from "./ownerUi";

// OWNER_FINANCE_PRO_V4_ECONOMY_ABM
// STATUS: STATIC_PASS · RUNTIME_PENDING
// TESTED: backend syntax + API backward compatibility. Full browser/build verification
// requires the complete repository dependencies, which are not included in this source zip.
// ARCHITECTURE: this page consumes the existing Owner finance ledger, category catalog
// and recurring-finance engine. Economy is a configuration/projection layer over those
// sources; it does not create a second financial source of truth.
// AI_READY_BASE: notes + the validated recurring-plan contract are intentionally kept
// as the future ingestion point for AI proposals. This patch does not let AI execute writes.

const NAV = [
  { value: "overview", label: "Resumen", icon: <WalletRoundedIcon fontSize="small" /> },
  { value: "movements", label: "Movimientos", icon: <ReceiptLongRoundedIcon fontSize="small" /> },
  { value: "economy", label: "Economía", icon: <SavingsRoundedIcon fontSize="small" /> },
  { value: "stats", label: "Estadísticas", icon: <QueryStatsRoundedIcon fontSize="small" /> },
  { value: "categories", label: "Categorías", icon: <CategoryRoundedIcon fontSize="small" /> },
];

const TYPE_LABELS = { income: "Ingreso", expense: "Gasto", transfer: "Transferencia", adjustment: "Ajuste" };
const SOURCE_LABELS = { manual: "Manual", recurring: "Recurrente", automation: "Automatización", project: "Proyecto", sale: "Venta", import: "Importado" };
const KIND_LABELS = { expense: "Gastos", income: "Ingresos", both: "Ingresos y gastos" };
const RECURRING_FREQUENCY_LABELS = { weekly: "Semanal", monthly: "Mensual", quarterly: "Trimestral", yearly: "Anual" };
const RECURRING_STATUS_LABELS = { active: "Activo", paused: "Pausado", ended: "Finalizado" };

function dateInputValue(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 10);
}

function startOfMonthValue() {
  const now = new Date();
  return dateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
}

function startOfYearValue() {
  const now = new Date();
  return dateInputValue(new Date(now.getFullYear(), 0, 1));
}

function thirtyDaysAgoValue() {
  const date = new Date();
  date.setDate(date.getDate() - 29);
  return dateInputValue(date);
}

function movementTitle(row) {
  return row?.description || row?.category || TYPE_LABELS[row?.type] || "Movimiento";
}

function transactionAccountId(row) {
  return idOf(row?.accountId || row?.fromAccountId || row?.toAccountId);
}

function canEditAmount(row) {
  const sourceKind = String(row?.source?.kind || "manual");
  return ["manual", "import", "project"].includes(sourceKind)
    && !row?.saleId
    && !row?.projectTermId
    && !row?.recurringPlanId
    && !row?.inventory?.movementId;
}

function StatCard({ label, value, helper }) {
  return <Box sx={{ ...surfaceSx, p: 1.05, minWidth: 0 }}>
    <Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950 }}>{label}</Typography>
    <Typography sx={{ mt: .25, fontWeight: 1000, fontSize: "1.08rem" }}>{value}</Typography>
    {helper ? <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".64rem" }}>{helper}</Typography> : null}
  </Box>;
}

function MovementDialog({ open, type, accounts, categories, clients, projects, onClose, onSaved }) {
  const [form, setForm] = React.useState({ amount: "", accountId: "", description: "", category: "", area: "Personal", occurredAt: dateTimeLocalValue(), clientId: "", initiativeId: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setForm({ amount: "", accountId: idOf(accounts.find((row) => !row.archived)) || "", description: "", category: "", area: "Personal", occurredAt: dateTimeLocalValue(), clientId: "", initiativeId: "" });
    setError("");
  }, [open, accounts]);

  const submit = async () => {
    if (!(Number(form.amount) > 0)) { setError("Ingresá un monto válido."); return; }
    if (!form.accountId) { setError("Elegí la cuenta."); return; }
    setSaving(true); setError("");
    try {
      await ownerApi.createFinanceTransaction({
        type,
        amount: Number(form.amount),
        accountId: form.accountId,
        description: form.description.trim(),
        category: form.category.trim(),
        area: form.area.trim() || "Personal",
        occurredAt: new Date(form.occurredAt).toISOString(),
        clientId: form.clientId || undefined,
        initiativeId: form.initiativeId || undefined,
        source: { kind: form.initiativeId ? "project" : "manual" },
      });
      await onSaved?.();
      onClose?.();
    } catch (err) { setError(formatApiError(err, "No pude registrar el movimiento.")); }
    finally { setSaving(false); }
  };

  const categoryRows = categories.filter((row) => !row.archived && [type, "both"].includes(row.kind));
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 1000 }}>{type === "income" ? "Nuevo ingreso" : "Nuevo gasto"}</DialogTitle>
    <DialogContent>
      <Stack spacing={1.1} sx={{ pt: .4 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth autoFocus type="number" label="Monto" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <TextField fullWidth select label="Cuenta" value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })}>
            {accounts.filter((row) => !row.archived).map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name} · {row.currency}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField label="Qué pasó" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth select label="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <MenuItem value="">Sin categoría</MenuItem>
            {categoryRows.map((row) => <MenuItem key={idOf(row)} value={row.name}>{row.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Área" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
        </Stack>
        <TextField type="datetime-local" label="Fecha" InputLabelProps={{ shrink: true }} value={form.occurredAt} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth select label="Cliente (opcional)" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
            <MenuItem value="">Sin cliente</MenuItem>
            {clients.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth select label="Proyecto (opcional)" value={form.initiativeId} onChange={(e) => setForm({ ...form, initiativeId: e.target.value })}>
            <MenuItem value="">Sin proyecto</MenuItem>
            {projects.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}</MenuItem>)}
          </TextField>
        </Stack>
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Guardando…" : "Registrar"}</Button></DialogActions>
  </Dialog>;
}

function AccountDialog({ open, onClose, onSaved }) {
  const [form, setForm] = React.useState({ name: "", type: "bank", currency: "ARS", openingBalance: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => { if (open) { setForm({ name: "", type: "bank", currency: "ARS", openingBalance: "" }); setError(""); } }, [open]);
  const submit = async () => {
    if (!form.name.trim()) { setError("Poné un nombre a la cuenta."); return; }
    setSaving(true); setError("");
    try { await ownerApi.createFinanceAccount({ ...form, openingBalance: Number(form.openingBalance || 0) }); await onSaved?.(); onClose?.(); }
    catch (err) { setError(formatApiError(err, "No pude crear la cuenta.")); }
    finally { setSaving(false); }
  };
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs"><DialogTitle sx={{ fontWeight: 1000 }}>Nueva cuenta</DialogTitle><DialogContent><Stack spacing={1.1} sx={{ pt: .4 }}>{error ? <Alert severity="error">{error}</Alert> : null}<TextField autoFocus label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /><TextField select label="Tipo" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><MenuItem value="bank">Banco</MenuItem><MenuItem value="wallet">Billetera</MenuItem><MenuItem value="cash">Efectivo</MenuItem><MenuItem value="investment">Inversión</MenuItem><MenuItem value="other">Otra</MenuItem></TextField><Stack direction="row" spacing={1}><TextField fullWidth label="Moneda" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /><TextField fullWidth type="number" label="Saldo inicial" value={form.openingBalance} onChange={(e) => setForm({ ...form, openingBalance: e.target.value })} /></Stack></Stack></DialogContent><DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Guardando…" : "Crear"}</Button></DialogActions></Dialog>;
}

function MovementEditDialog({ row, open, accounts, categories, clients, projects, onClose, onSaved }) {
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open || !row) return;
    setForm({
      amount: String(row.amount || ""),
      accountId: transactionAccountId(row),
      description: row.description || "",
      category: row.category || "",
      subcategory: row.subcategory || "",
      area: row.area || "Personal",
      occurredAt: dateTimeLocalValue(row.occurredAt),
      clientId: idOf(row.relations?.client) || "",
      initiativeId: idOf(row.relations?.initiative) || "",
    });
    setError("");
  }, [open, row]);

  if (!row) return null;
  const amountEditable = canEditAmount(row);
  const saleProtected = Boolean(row.saleId || row.source?.kind === "sale" || row.relations?.saleProtected);
  const relationProtected = Boolean(saleProtected || row.recurringPlanId || row.projectTermId || row.source?.kind === "recurring" || row.relations?.recurringPlan);
  const categoryRows = categories.filter((item) => !item.archived && [row.type, "both"].includes(item.kind));
  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        description: form.description.trim(), category: form.category.trim(), subcategory: form.subcategory.trim(), area: form.area.trim() || "Personal",
      };
      if (!saleProtected) {
        payload.occurredAt = new Date(form.occurredAt).toISOString();
        if (row.type !== "transfer" && form.accountId) payload.accountId = form.accountId;
      }
      if (!relationProtected) {
        payload.clientId = form.clientId || null;
        payload.initiativeId = form.initiativeId || null;
      }
      if (amountEditable) payload.amount = Number(form.amount);
      await ownerApi.updateFinanceTransaction(idOf(row), payload);
      await onSaved?.();
      onClose?.();
    } catch (err) { setError(formatApiError(err, "No pude editar el movimiento.")); }
    finally { setSaving(false); }
  };

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
    <DialogTitle sx={{ fontWeight: 1000 }}>Detalle y edición del movimiento</DialogTitle>
    <DialogContent>
      <Stack spacing={1.15} sx={{ pt: .4 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Box sx={{ ...surfaceSx, p: .9 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Box><Typography sx={{ fontWeight: 1000 }}>{movementTitle(row)}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>{TYPE_LABELS[row.type] || row.type} · {SOURCE_LABELS[row.source?.kind] || row.source?.kind || "Manual"} · ID {idOf(row).slice(-8)}</Typography></Box>
            <Typography sx={{ fontWeight: 1000, fontSize: "1rem" }}>{row.type === "expense" ? "−" : row.type === "income" ? "+" : ""}{money(row.amount, row.currency)}</Typography>
          </Stack>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth type="number" label="Monto" value={form.amount || ""} disabled={!amountEditable} helperText={!amountEditable ? "Monto protegido porque el movimiento está vinculado a otro registro." : "Editable en movimientos manuales/importados sin vínculos contables."} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          {row.type !== "transfer" ? <TextField fullWidth select disabled={saleProtected} label="Cuenta" value={form.accountId || ""} onChange={(e) => setForm({ ...form, accountId: e.target.value })}>{accounts.filter((item) => !item.archived && item.currency === row.currency).map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}</MenuItem>)}</TextField> : null}
        </Stack>
        <TextField label="Descripción" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth select label="Categoría" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })}><MenuItem value="">Sin categoría</MenuItem>{categoryRows.map((item) => <MenuItem key={idOf(item)} value={item.name}>{item.name}</MenuItem>)}</TextField>
          <TextField fullWidth label="Subcategoría" value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} />
          <TextField fullWidth label="Área" value={form.area || ""} onChange={(e) => setForm({ ...form, area: e.target.value })} />
        </Stack>
        <TextField disabled={saleProtected} type="datetime-local" label="Fecha" InputLabelProps={{ shrink: true }} value={form.occurredAt || ""} helperText={saleProtected ? "La fecha está protegida porque respalda un cobro de venta." : ""} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth select disabled={relationProtected} label="Cliente" value={form.clientId || ""} onChange={(e) => setForm({ ...form, clientId: e.target.value })}><MenuItem value="">Sin cliente</MenuItem>{clients.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}</MenuItem>)}</TextField>
          <TextField fullWidth select disabled={relationProtected} label="Proyecto" value={form.initiativeId || ""} onChange={(e) => setForm({ ...form, initiativeId: e.target.value })}><MenuItem value="">Sin proyecto</MenuItem>{projects.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{item.name}</MenuItem>)}</TextField>
        </Stack>
        <Divider />
        <Stack direction="row" spacing={.5} useFlexGap flexWrap="wrap">
          {row.category ? <Chip size="small" label={`Categoría: ${row.category}`} /> : null}
          {row.relations?.client?.name ? <Chip size="small" variant="outlined" label={`Cliente: ${row.relations.client.name}`} /> : null}
          {row.relations?.initiative?.name ? <Chip size="small" variant="outlined" label={`Proyecto: ${row.relations.initiative.name}`} /> : null}
          {row.relations?.sale?.title ? <Chip size="small" variant="outlined" label={`Venta: ${row.relations.sale.title}`} /> : null}
          {row.relations?.recurringPlan?.name ? <Chip size="small" variant="outlined" label={`Mensualidad: ${row.relations.recurringPlan.name}`} /> : null}
          {row.inventory?.itemName ? <Chip size="small" variant="outlined" label={`Stock: ${row.inventory.itemName}`} /> : null}
        </Stack>
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Cerrar</Button><Button variant="contained" onClick={save} disabled={saving || (amountEditable && !(Number(form.amount) > 0))}>{saving ? "Guardando…" : "Guardar cambios"}</Button></DialogActions>
  </Dialog>;
}

function CategoryRenameDialog({ category, open, onClose, onSaved }) {
  const [name, setName] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => { if (open && category) { setName(category.name || ""); setError(""); } }, [open, category]);
  if (!category) return null;
  const submit = async () => {
    if (!name.trim()) { setError("Ingresá un nombre."); return; }
    setSaving(true); setError("");
    try {
      if (category.detected) {
        await ownerApi.createFinanceCategory({ name: name.trim(), kind: category.kind, renameFrom: category.name });
      } else {
        await ownerApi.updateFinanceCategory(idOf(category), { name: name.trim(), renameHistory: true });
      }
      await onSaved?.(); onClose?.();
    } catch (err) { setError(formatApiError(err, "No pude renombrar la categoría.")); }
    finally { setSaving(false); }
  };
  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs"><DialogTitle sx={{ fontWeight: 1000 }}>Renombrar categoría</DialogTitle><DialogContent><Stack spacing={1.1} sx={{ pt: .4 }}>{error ? <Alert severity="error">{error}</Alert> : null}<Alert severity="info">Se actualizarán todos los movimientos de <strong>{category.name}</strong> dentro de {KIND_LABELS[category.kind] || category.kind}. El monto y la fecha de cada movimiento no cambian.</Alert><TextField autoFocus label="Nuevo nombre" value={name} onChange={(e) => setName(e.target.value)} /></Stack></DialogContent><DialogActions><Button onClick={onClose} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Renombrando…" : "Renombrar grupo"}</Button></DialogActions></Dialog>;
}

function MovementRows({ rows, accounts, onEdit }) {
  const accountMap = React.useMemo(() => new Map(accounts.map((row) => [idOf(row), row])), [accounts]);
  if (!rows.length) return <Typography sx={{ color: "text.secondary", fontSize: ".76rem", py: 2, textAlign: "center" }}>No hay movimientos con estos filtros.</Typography>;
  return <Stack spacing={.35}>{rows.map((row) => {
    const relation = row.relations?.initiative?.name || row.relations?.client?.name || row.relations?.sale?.title || "";
    const account = accountMap.get(transactionAccountId(row));
    return <Box key={idOf(row)} sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr) auto", md: "minmax(0,1.8fr) minmax(110px,.7fr) minmax(120px,.8fr) auto" }, gap: .7, alignItems: "center", px: .8, py: .7, border: "1px solid", borderColor: "divider", borderRadius: 1.7, bgcolor: "background.paper", "&:hover": { bgcolor: "action.hover" } }}>
      <Box sx={{ minWidth: 0 }}><Stack direction="row" spacing={.45} alignItems="center" useFlexGap flexWrap="wrap"><Typography noWrap sx={{ minWidth: 0, fontWeight: 950, fontSize: ".78rem" }}>{movementTitle(row)}</Typography><Chip size="small" label={TYPE_LABELS[row.type] || row.type} sx={{ height: 19, fontSize: ".58rem" }} /></Stack><Typography noWrap sx={{ color: "text.secondary", fontSize: ".62rem", mt: .15 }}>{shortDate(row.occurredAt)}{row.category ? ` · ${row.category}` : ""}{relation ? ` · ${relation}` : ""}</Typography></Box>
      <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}><Typography noWrap sx={{ fontSize: ".7rem", fontWeight: 850 }}>{account?.name || "—"}</Typography><Typography noWrap sx={{ color: "text.secondary", fontSize: ".6rem" }}>{row.currency || "ARS"}</Typography></Box>
      <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}><Typography noWrap sx={{ fontSize: ".7rem", fontWeight: 850 }}>{row.area || "Personal"}</Typography><Typography noWrap sx={{ color: "text.secondary", fontSize: ".6rem" }}>{row.subcategory || "Sin subcategoría"}</Typography></Box>
      <Stack direction="row" spacing={.35} alignItems="center" justifyContent="flex-end"><Typography sx={{ fontWeight: 1000, fontSize: ".8rem", whiteSpace: "nowrap" }}>{row.type === "expense" ? "−" : row.type === "income" ? "+" : ""}{money(row.amount, row.currency)}</Typography><Tooltip title="Ver y editar"><IconButton size="small" onClick={() => onEdit(row)}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip></Stack>
    </Box>;
  })}</Stack>;
}

function statsDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const normalized = /^\d{4}-\d{2}$/.test(raw) ? `${raw}-01` : raw.slice(0, 10);
  const date = new Date(`${normalized}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addStatsPeriod(date, bucket = "day", amount = 1) {
  const next = new Date(date);
  if (bucket === "week") next.setDate(next.getDate() + (7 * amount));
  else if (bucket === "month") next.setMonth(next.getMonth() + amount);
  else next.setDate(next.getDate() + amount);
  return next;
}

function addRecurringOccurrence(date, frequency = "monthly") {
  const next = new Date(date);
  if (frequency === "weekly") next.setDate(next.getDate() + 7);
  else if (frequency === "quarterly") next.setMonth(next.getMonth() + 3);
  else if (frequency === "yearly") next.setFullYear(next.getFullYear() + 1);
  else next.setMonth(next.getMonth() + 1);
  return next;
}

function recurringOccurrencesInWindow(plan, windowStart, windowEnd) {
  const anchor = statsDate(plan?.firstDueDate || plan?.startDate || plan?.nextDueAt);
  if (!anchor || !windowStart || !windowEnd || windowEnd <= windowStart) return 0;
  const planStart = statsDate(plan?.startDate) || anchor;
  const planEnd = statsDate(plan?.endDate);
  const start = new Date(Math.max(windowStart.getTime(), planStart.getTime()));
  const end = new Date(Math.min(windowEnd.getTime(), planEnd ? planEnd.getTime() + 86400000 : windowEnd.getTime()));
  if (end <= start) return 0;

  let cursor = new Date(anchor);
  let guard = 0;
  while (cursor < start && guard < 6000) {
    cursor = addRecurringOccurrence(cursor, plan?.frequency);
    guard += 1;
  }
  let count = 0;
  while (cursor < end && guard < 6000) {
    count += 1;
    cursor = addRecurringOccurrence(cursor, plan?.frequency);
    guard += 1;
  }
  return count;
}

function StatsView({ stats, economy, loading, accounts, categories, filter, setFilter, onReload }) {
  // WP-OWNER [STATS_V6_ECONOMY_HYBRID]
  // STATUS: STATIC_REVIEWED · RUNTIME_PENDING
  // UX CONTRACT: one anti-scroll chart only. Bars are actual ledger values; the solid line
  // is the recent real average and the lighter dashed line comes from active Economy plans.
  // Growth only lifts projected INCOME; it never inflates projected expenses automatically.
  const [dimension, setDimension] = React.useState("period");
  const [metric, setMetric] = React.useState("net");
  const [growthPct, setGrowthPct] = React.useState(0);
  const [showAverage, setShowAverage] = React.useState(true);
  const [showTarget, setShowTarget] = React.useState(true);
  const series = React.useMemo(() => (stats?.series || []).slice(-36), [stats]);
  const breakdown = React.useMemo(() => stats?.categoryBreakdown || [], [stats]);
  const currency = stats?.filters?.currency || filter.currency || "ARS";

  const categorySeries = React.useMemo(() => {
    const grouped = new Map();
    breakdown.forEach((row) => {
      const key = String(row?.name || "Sin categoría").trim() || "Sin categoría";
      const current = grouped.get(key) || { name: key, income: 0, expense: 0, count: 0 };
      const amount = Number(row?.amount || 0);
      if (row?.type === "income") current.income += amount;
      if (row?.type === "expense") current.expense += amount;
      current.count += Number(row?.count || 0);
      grouped.set(key, current);
    });
    return [...grouped.values()]
      .map((row) => ({ ...row, net: row.income - row.expense }))
      .sort((a, b) => Math.abs(Number(b[metric] || 0)) - Math.abs(Number(a[metric] || 0)));
  }, [breakdown, metric]);

  const applyFilter = React.useCallback((next) => {
    setFilter(next);
    onReload?.(next);
  }, [onReload, setFilter]);

  const applyPreset = React.useCallback((preset) => {
    const today = dateInputValue();
    const next = preset === "30d"
      ? { ...filter, start: thirtyDaysAgoValue(), end: today, bucket: "day" }
      : preset === "month"
        ? { ...filter, start: startOfMonthValue(), end: today, bucket: "day" }
        : { ...filter, start: startOfYearValue(), end: today, bucket: "month" };
    applyFilter(next);
  }, [applyFilter, filter]);

  const clearCategory = React.useCallback(() => {
    if (!filter.category) return;
    applyFilter({ ...filter, category: "" });
  }, [applyFilter, filter]);

  const periodMetricRows = React.useMemo(() => {
    const rows = series.map((row) => ({ ...row, chartValue: Number(row?.[metric] || 0) }));
    return rows.map((row, index) => {
      const from = Math.max(0, index - 2);
      const sample = rows.slice(from, index + 1);
      const averageValue = sample.reduce((sum, item) => sum + Number(item.chartValue || 0), 0) / Math.max(1, sample.length);
      return { ...row, averageValue };
    });
  }, [series, metric]);

  const projectedRows = React.useMemo(() => {
    const plans = (economy?.plans || []).filter((plan) => (
      plan?.economyEligible !== false
      && String(plan?.effectiveStatus || plan?.status || "active") === "active"
      && String(plan?.currency || "ARS").toUpperCase() === String(currency || "ARS").toUpperCase()
      && (!filter.category || String(plan?.category || "") === String(filter.category))
      && (!filter.accountId || idOf(plan?.defaultAccountId) === String(filter.accountId))
    ));
    const growthFactor = 1 + (Number(growthPct || 0) / 100);
    return periodMetricRows.map((row, index) => {
      const start = statsDate(row?.period) || statsDate(filter.start);
      const nextPeriod = statsDate(periodMetricRows[index + 1]?.period);
      const end = nextPeriod || (start ? addStatsPeriod(start, filter.bucket || "day") : null);
      let projectedIncome = 0;
      let projectedExpense = 0;
      plans.forEach((plan) => {
        const occurrences = recurringOccurrencesInWindow(plan, start, end);
        if (!occurrences) return;
        const amount = recurringPlanAmount(plan) * occurrences;
        if (plan.direction === "income") projectedIncome += amount;
        if (plan.direction === "expense") projectedExpense += amount;
      });
      const targetIncome = projectedIncome * growthFactor;
      const targetValue = metric === "income" ? targetIncome : metric === "expense" ? projectedExpense : targetIncome - projectedExpense;
      return { ...row, projectedIncome, projectedExpense, targetValue };
    });
  }, [currency, economy, filter.accountId, filter.bucket, filter.category, filter.start, growthPct, metric, periodMetricRows]);

  const chartDomain = React.useMemo(() => {
    const values = [0];
    projectedRows.forEach((row) => {
      values.push(Number(row.chartValue || 0));
      if (showAverage) values.push(Number(row.averageValue || 0));
      if (showTarget) values.push(Number(row.targetValue || 0));
    });
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (min === max) { min -= 1; max += 1; }
    const pad = Math.max(1, (max - min) * .08);
    return { min: min - pad, max: max + pad };
  }, [projectedRows, showAverage, showTarget]);

  const yPercent = React.useCallback((value) => {
    const range = chartDomain.max - chartDomain.min || 1;
    return 100 - ((Number(value || 0) - chartDomain.min) / range * 100);
  }, [chartDomain]);

  const baselineY = yPercent(0);
  const averagePoints = React.useMemo(() => projectedRows.map((row, index) => `${((index + .5) / Math.max(1, projectedRows.length)) * 1000},${yPercent(row.averageValue)}`).join(" "), [projectedRows, yPercent]);
  const targetPoints = React.useMemo(() => projectedRows.map((row, index) => `${((index + .5) / Math.max(1, projectedRows.length)) * 1000},${yPercent(row.targetValue)}`).join(" "), [projectedRows, yPercent]);
  const targetHasData = React.useMemo(() => projectedRows.some((row) => Math.abs(Number(row.targetValue || 0)) > 0), [projectedRows]);

  const visibleCategoryRows = React.useMemo(() => {
    const primary = categorySeries.slice(0, 8).map((row) => ({ ...row, chartValue: Number(row?.[metric] || 0) }));
    if (categorySeries.length <= 8) return primary;
    const rest = categorySeries.slice(8).reduce((acc, row) => ({
      income: acc.income + Number(row.income || 0),
      expense: acc.expense + Number(row.expense || 0),
      net: acc.net + Number(row.net || 0),
      count: acc.count + Number(row.count || 0),
    }), { income: 0, expense: 0, net: 0, count: 0 });
    return [...primary, { name: `Otras (${categorySeries.length - 8})`, ...rest, chartValue: Number(rest?.[metric] || 0), synthetic: true }];
  }, [categorySeries, metric]);
  const maxCategoryValue = React.useMemo(() => Math.max(1, ...visibleCategoryRows.map((row) => Math.abs(row.chartValue))), [visibleCategoryRows]);

  const metricLabel = metric === "income" ? "Ingresos" : metric === "expense" ? "Gastos" : "Neto";
  const dimensionLabel = dimension === "period" ? "por período" : "por categoría";
  const chartEmpty = dimension === "period" ? !periodMetricRows.length : !visibleCategoryRows.length;

  return <Box sx={{
    display: "flex",
    flexDirection: "column",
    gap: .65,
    minHeight: 0,
    height: { xs: "calc(100dvh - 205px)", md: "calc(100dvh - 190px)" },
    maxHeight: { md: 720 },
    overflow: "hidden",
  }}>
    <Box sx={{ ...surfaceSx, p: .72, flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" spacing={.6} sx={{ minWidth: 0 }}>
        <Tabs
          value={dimension}
          onChange={(_event, value) => setDimension(value)}
          sx={{
            minHeight: 32,
            flexShrink: 0,
            "& .MuiTabs-indicator": { height: 2.5, borderRadius: 999 },
            "& .MuiTab-root": { minHeight: 32, minWidth: 0, px: 1.05, py: 0, textTransform: "none", fontWeight: 950, fontSize: ".68rem" },
          }}
        >
          <Tab value="period" label="Por período" />
          <Tab value="category" label="Por categoría" />
        </Tabs>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" spacing={.35} sx={{ overflowX: "auto", minWidth: 0, flex: 1, pb: .1, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          {[{ value: "net", label: "Neto" }, { value: "income", label: "Ingresos" }, { value: "expense", label: "Gastos" }].map((item) => <Chip
            key={item.value}
            size="small"
            label={item.label}
            variant={metric === item.value ? "filled" : "outlined"}
            color={metric === item.value ? "primary" : "default"}
            onClick={() => setMetric(item.value)}
            sx={{ height: 25, fontSize: ".62rem", fontWeight: 900, flexShrink: 0 }}
          />)}
          <Divider orientation="vertical" flexItem sx={{ mx: .15 }} />
          <Chip size="small" label="30 días" onClick={() => applyPreset("30d")} sx={{ height: 25, fontSize: ".6rem", flexShrink: 0 }} />
          <Chip size="small" label="Este mes" onClick={() => applyPreset("month")} sx={{ height: 25, fontSize: ".6rem", flexShrink: 0 }} />
          <Chip size="small" label="Este año" onClick={() => applyPreset("year")} sx={{ height: 25, fontSize: ".6rem", flexShrink: 0 }} />
          {filter.category ? <Chip size="small" color="primary" variant="outlined" label={`Categoría: ${filter.category} ×`} onClick={clearCategory} sx={{ height: 25, fontSize: ".6rem", fontWeight: 900, flexShrink: 0 }} /> : null}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={.45}
        sx={{
          mt: .6,
          overflowX: "auto",
          pb: .15,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& .MuiInputBase-root": { height: 34, fontSize: ".7rem" },
          "& .MuiInputLabel-root": { fontSize: ".69rem" },
        }}
      >
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={filter.start} onChange={(e) => setFilter({ ...filter, start: e.target.value })} sx={{ minWidth: 132 }} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={filter.end} onChange={(e) => setFilter({ ...filter, end: e.target.value })} sx={{ minWidth: 132 }} />
        {dimension === "period" ? <TextField size="small" select label="Agrupar" value={filter.bucket} onChange={(e) => setFilter({ ...filter, bucket: e.target.value })} sx={{ minWidth: 108 }}>
          <MenuItem value="day">Día</MenuItem><MenuItem value="week">Semana</MenuItem><MenuItem value="month">Mes</MenuItem>
        </TextField> : null}
        <TextField size="small" select label="Moneda" value={filter.currency || "ARS"} onChange={(e) => setFilter({ ...filter, currency: e.target.value })} sx={{ minWidth: 94 }}>
          {[...new Set(accounts.map((row) => row.currency).filter(Boolean))].map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Cuenta" value={filter.accountId} onChange={(e) => setFilter({ ...filter, accountId: e.target.value })} sx={{ minWidth: 148 }}>
          <MenuItem value="">Todas</MenuItem>{accounts.filter((row) => !row.archived).map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Categoría" value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todas</MenuItem>{categories.filter((row) => !row.archived).map((row) => <MenuItem key={`${row.kind}:${idOf(row)}`} value={row.name}>{row.name}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={() => onReload?.(filter)} disabled={loading} sx={{ minWidth: 82, height: 34, textTransform: "none", fontWeight: 950, fontSize: ".68rem", flexShrink: 0 }}>Aplicar</Button>
      </Stack>
    </Box>

    <Box sx={{ ...surfaceSx, px: .8, py: .62, flexShrink: 0, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(118px,1fr))", minWidth: 510, gap: .45 }}>
        {[
          ["INGRESOS", money(stats?.summary?.income || 0, currency)],
          ["GASTOS", money(stats?.summary?.expense || 0, currency)],
          ["NETO", money(stats?.summary?.net || 0, currency)],
          ["MOVIMIENTOS", stats?.summary?.count || 0],
        ].map(([label, value]) => <Box key={label} sx={{ minWidth: 0, px: .72, py: .25, borderRight: label === "MOVIMIENTOS" ? 0 : "1px solid", borderColor: "divider" }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".55rem", fontWeight: 950, letterSpacing: ".04em" }}>{label}</Typography>
          <Typography noWrap sx={{ mt: .05, fontWeight: 1000, fontSize: ".86rem" }}>{value}</Typography>
        </Box>)}
      </Box>
    </Box>

    <Box sx={{ ...surfaceSx, p: .82, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={.45} sx={{ mb: .45, flexShrink: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 1000, fontSize: ".78rem" }}>{metricLabel} {dimensionLabel}</Typography>
          <Typography noWrap sx={{ color: "text.secondary", fontSize: ".58rem" }}>
            {filter.start || "Inicio"} → {filter.end || "Hoy"}{filter.accountId ? " · cuenta filtrada" : " · todas las cuentas"}{filter.category ? ` · ${filter.category}` : ""}
          </Typography>
        </Box>
        {dimension === "period" ? <Stack direction="row" spacing={.3} sx={{ overflowX: "auto", maxWidth: "100%", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          <Chip size="small" label="Barras · real" sx={{ height: 22, fontSize: ".56rem", fontWeight: 900, flexShrink: 0 }} />
          <Chip size="small" variant={showAverage ? "filled" : "outlined"} color={showAverage ? "primary" : "default"} label="Línea · promedio real" onClick={() => setShowAverage((value) => !value)} sx={{ height: 22, fontSize: ".56rem", fontWeight: 900, flexShrink: 0 }} />
          <Chip size="small" variant={showTarget ? "filled" : "outlined"} color={showTarget ? "secondary" : "default"} label="Línea · Economía" onClick={() => setShowTarget((value) => !value)} sx={{ height: 22, fontSize: ".56rem", fontWeight: 900, flexShrink: 0 }} />
          {[0, 10, 20, 30].map((value) => <Chip key={value} size="small" variant={growthPct === value ? "filled" : "outlined"} label={value ? `+${value}%` : "Base"} onClick={() => setGrowthPct(value)} sx={{ height: 22, fontSize: ".54rem", fontWeight: 900, flexShrink: 0 }} />)}
        </Stack> : <Typography sx={{ color: "text.secondary", fontSize: ".58rem", whiteSpace: "nowrap" }}>{categorySeries.length} categorías</Typography>}
      </Stack>

      {loading ? <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}><CircularProgress size={28} /></Stack> : chartEmpty ? <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}><Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Sin datos para estos filtros.</Typography></Stack> : dimension === "period" ? <Box sx={{ flex: 1, minHeight: 0, position: "relative", pt: .6, pb: 2.15, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", left: 0, right: 0, top: `calc(${baselineY}% + 6px)`, borderTop: "1px solid", borderColor: "divider", opacity: .8, zIndex: 0 }} />
        <Box sx={{ position: "absolute", inset: "6px 0 27px 0", zIndex: 2, pointerEvents: "none" }}>
          {showAverage && averagePoints ? <Box component="svg" viewBox="0 0 1000 100" preserveAspectRatio="none" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible", color: "primary.main" }}>
            <polyline points={averagePoints} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </Box> : null}
          {showTarget && targetHasData && targetPoints ? <Box component="svg" viewBox="0 0 1000 100" preserveAspectRatio="none" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible", color: "secondary.main", opacity: .46 }}>
            <polyline points={targetPoints} fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="7 7" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </Box> : null}
        </Box>
        <Box sx={{ position: "absolute", inset: "6px 0 27px 0", display: "flex", gap: { xs: .2, md: .34 }, alignItems: "stretch", zIndex: 1 }}>
          {projectedRows.map((row, index) => {
            const value = Number(row.chartValue || 0);
            const valueY = yPercent(value);
            const top = Math.min(valueY, baselineY);
            const height = Math.max(1.4, Math.abs(valueY - baselineY));
            const labelEvery = Math.max(1, Math.ceil(projectedRows.length / 9));
            return <Tooltip key={`${row.period}:${index}`} arrow title={`${shortDate(row.period)} · Real ${money(value, currency)} · Promedio ${money(row.averageValue || 0, currency)}${targetHasData ? ` · Economía ${growthPct ? `+${growthPct}% ` : ""}${money(row.targetValue || 0, currency)}` : ""} · Ingresos ${money(row.income || 0, currency)} · Gastos ${money(row.expense || 0, currency)}`}>
              <Box sx={{ flex: 1, minWidth: 4, height: "100%", position: "relative", cursor: "default", "&:hover .wp-stat-bar": { opacity: 1 } }}>
                <Box className="wp-stat-bar" sx={{
                  position: "absolute",
                  left: "20%",
                  right: "20%",
                  top: `${top}%`,
                  height: `${height}%`,
                  minHeight: 2,
                  borderRadius: value >= 0 ? "5px 5px 2px 2px" : "2px 2px 5px 5px",
                  bgcolor: metric === "income" ? "success.main" : metric === "expense" ? "error.main" : value >= 0 ? "success.main" : "error.main",
                  opacity: .68,
                  transition: "opacity .15s ease, transform .15s ease",
                }} />
                {index % labelEvery === 0 || index === projectedRows.length - 1 ? <Typography sx={{ position: "absolute", top: "calc(100% + 5px)", left: "50%", transform: "translateX(-50%)", color: "text.secondary", fontSize: ".5rem", whiteSpace: "nowrap" }}>{shortDate(row.period)}</Typography> : null}
              </Box>
            </Tooltip>;
          })}
        </Box>
        {showTarget && !targetHasData ? <Typography sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary", fontSize: ".55rem", zIndex: 3 }}>Economía sin recurrentes activos para {currency}</Typography> : null}
      </Box> : <Stack spacing={.38} sx={{ flex: 1, minHeight: 0, justifyContent: "center", overflow: "hidden" }}>
        {visibleCategoryRows.map((row) => {
          const value = row.chartValue;
          const positive = value >= 0;
          const width = Math.max(2, Math.abs(value) / maxCategoryValue * 100);
          return <Tooltip key={row.name} arrow title={`${row.name} · ${metricLabel}: ${money(value, currency)} · ${row.count} movimientos`}>
            <Box
              onClick={() => { if (!row.synthetic && row.name !== filter.category) applyFilter({ ...filter, category: row.name }); }}
              sx={{ display: "grid", gridTemplateColumns: { xs: "92px minmax(0,1fr) 82px", sm: "145px minmax(0,1fr) 110px" }, gap: .65, alignItems: "center", minHeight: 27, cursor: row.synthetic ? "default" : "pointer", "&:hover .wp-category-track": { bgcolor: "action.selected" } }}
            >
              <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 900, fontSize: ".63rem" }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".5rem" }}>{row.count} mov.</Typography></Box>
              <Box className="wp-category-track" sx={{ height: 9, borderRadius: 999, bgcolor: "action.hover", overflow: "hidden", transition: "background-color .15s ease" }}>
                <Box sx={{ height: "100%", width: `${width}%`, borderRadius: 999, bgcolor: metric === "income" ? "success.main" : metric === "expense" ? "error.main" : positive ? "success.main" : "error.main", opacity: .76 }} />
              </Box>
              <Typography sx={{ textAlign: "right", fontWeight: 950, fontSize: ".62rem", whiteSpace: "nowrap" }}>{money(value, currency)}</Typography>
            </Box>
          </Tooltip>;
        })}
      </Stack>}
    </Box>
  </Box>;
}

// OWNER_ECONOMY_RECURRING_ABM_V4
// STATUS: STATIC_PASS · RUNTIME_PENDING
// CONTRACT: Economy is a management layer over OwnerFinanceRecurringPlan. It never
// duplicates ledger data. Manual plans can be created/edited/paused/deleted here;
// generated contract/infrastructure plans stay read-only and are managed at origin.
function recurringPlanDraft(plan = null, direction = "expense") {
  const today = dateInputValue();
  const firstDue = plan?.firstDueDate ? dateInputValue(plan.firstDueDate) : today;
  const amountMode = plan?.amountMode || "fixed";
  return {
    name: plan?.name || "",
    direction: plan?.direction || direction,
    amountMode,
    fixedAmount: String(plan?.fixedAmount || (amountMode === "fixed" ? plan?.projectedAmount || "" : "")),
    estimatedAmount: String(plan?.estimatedAmount || (amountMode === "variable" ? plan?.projectedAmount || "" : "")),
    currency: String(plan?.currency || "ARS").toUpperCase(),
    defaultAccountId: idOf(plan?.defaultAccountId) || "",
    clientId: idOf(plan?.clientId) || "",
    serviceId: idOf(plan?.serviceId) || "",
    category: plan?.category || "",
    area: plan?.area || "Personal",
    frequency: plan?.frequency || "monthly",
    startDate: plan?.startDate ? dateInputValue(plan.startDate) : today,
    firstDueDate: firstDue,
    dueDay: String(plan?.dueDay || Number(firstDue.slice(-2)) || new Date().getDate()),
    endDate: plan?.endDate ? dateInputValue(plan.endDate) : "",
    status: plan?.effectiveStatus || plan?.status || "active",
    notes: plan?.notes || "",
  };
}

function recurringPlanAmount(plan) {
  if (plan?.amountMode === "variable") return Number(plan?.estimatedAmount || plan?.projectedAmount || 0);
  return Number(plan?.fixedAmount || plan?.projectedAmount || 0);
}

// OWNER_ECONOMY_JSON_HOTFIX_V1
// STATUS: STATIC_PASS · RUNTIME_PENDING
// PURPOSE: portable inspection/backup contract for Economy without creating a second
// backend import engine. System-generated Billing/contract/infrastructure rows are
// exported for diagnosis, but intentionally NEVER recreated by JSON import.
function economyPlanKey(plan = {}) {
  return [
    String(plan.direction || "expense"),
    String(plan.name || "").trim().toLowerCase(),
    String(plan.currency || "ARS").toUpperCase(),
    String(plan.frequency || "monthly"),
    dateInputValue(plan.startDate || ""),
    idOf(plan.clientId) || "",
    idOf(plan.serviceId) || "",
  ].join("|");
}

function economyPortablePlan(plan = {}, { diagnostic = false } = {}) {
  const amountMode = plan.amountMode === "variable" ? "variable" : "fixed";
  return {
    name: String(plan.name || "").trim(),
    direction: plan.direction === "income" ? "income" : "expense",
    amountMode,
    fixedAmount: amountMode === "fixed" ? Number(plan.fixedAmount || plan.projectedAmount || 0) : null,
    estimatedAmount: amountMode === "variable" ? Number(plan.estimatedAmount || plan.projectedAmount || 0) : null,
    currency: String(plan.currency || "ARS").toUpperCase(),
    defaultAccountId: idOf(plan.defaultAccountId) || null,
    defaultAccountName: plan.defaultAccountId?.name || null,
    clientId: idOf(plan.clientId) || null,
    clientName: plan.clientId?.name || plan.clientId?.company || null,
    serviceId: idOf(plan.serviceId) || null,
    serviceName: plan.serviceId?.name || null,
    category: String(plan.category || ""),
    area: String(plan.area || "Personal"),
    frequency: String(plan.frequency || "monthly"),
    startDate: dateInputValue(plan.startDate || ""),
    firstDueDate: dateInputValue(plan.firstDueDate || plan.startDate || ""),
    dueDay: Number(plan.dueDay || 1),
    endDate: plan.endDate ? dateInputValue(plan.endDate) : null,
    status: String(plan.effectiveStatus || plan.status || "active"),
    notes: String(plan.notes || ""),
    ...(diagnostic ? {
      sourceId: idOf(plan) || null,
      hasHistory: Boolean(plan.hasHistory),
      economyEligible: plan.economyEligible !== false,
      activationState: String(plan.activationState || "active"),
      firstPaymentAt: plan.firstPaymentAt || null,
      activationReason: String(plan.activationReason || ""),
      billingIdentity: plan.billingIdentity || null,
      billingGroupRole: String(plan.billingGroupRole || ""),
      billingGroup: plan.billingGroup || null,
      consolidatedIntoPlanId: plan.consolidatedIntoPlanId || null,
      origin: plan.origin || null,
      nextDueAt: plan.nextDueAt || null,
      projectedAmount: Number(plan.projectedAmount || 0),
    } : {}),
  };
}

function normalizeEconomyImport(raw) {
  const envelope = Array.isArray(raw) ? { manualPlans: raw } : (raw || {});
  const source = Array.isArray(envelope.manualPlans)
    ? envelope.manualPlans
    : Array.isArray(envelope.plans) ? envelope.plans : [];
  const generatedSkipped = [
    ...(Array.isArray(envelope.generatedPlans) ? envelope.generatedPlans : []),
    ...source.filter((row) => row?.origin?.generated),
  ].length;
  const manualSource = source.filter((row) => !row?.origin?.generated);
  const valid = [];
  const invalid = [];
  manualSource.forEach((row, index) => {
    const portable = economyPortablePlan(row);
    const amount = portable.amountMode === "variable" ? portable.estimatedAmount : portable.fixedAmount;
    const reasons = [];
    if (!portable.name) reasons.push("sin nombre");
    if (!['expense', 'income'].includes(portable.direction)) reasons.push("tipo inválido");
    if (!['weekly', 'monthly', 'quarterly', 'yearly'].includes(portable.frequency)) reasons.push("frecuencia inválida");
    if (!(Number(amount) > 0)) reasons.push("monto inválido");
    if (!portable.startDate || !portable.firstDueDate) reasons.push("fechas incompletas");
    if (portable.direction === "income" && (!portable.clientId || !portable.serviceId)) reasons.push("ingreso sin cliente/servicio");
    if (reasons.length) invalid.push({ index, name: portable.name || `Fila ${index + 1}`, reasons });
    else valid.push(portable);
  });
  return { valid, invalid, generatedSkipped };
}

// OWNER_ECONOMY_CATEGORY_HISTORY_V1
// STATUS: STATIC_PASS · RUNTIME_PENDING
// UX CONTRACT: selecting an expense category inspects the real ledger, suggests the
// historical start + next due date, and can use average spend per recurrence period.
// It never creates or rewrites historical movements automatically.
function RecurringPlanDialog({ open, plan, direction = "expense", accounts, categories, clients, services, onClose, onSaved }) {
  const [form, setForm] = React.useState(recurringPlanDraft(plan, direction));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [categoryHistory, setCategoryHistory] = React.useState(null);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState("");
  const [useHistoricalAverage, setUseHistoricalAverage] = React.useState(false);
  const dateTouchedRef = React.useRef({ startDate: false, firstDueDate: false, dueDay: false });
  const editing = Boolean(plan?.id || plan?._id);
  const generated = Boolean(plan?.origin?.generated);
  const structureLocked = Boolean(editing && plan?.hasHistory);
  const isIncome = String(plan?.direction || direction) === "income";
  const allowVariable = isIncome || (!useHistoricalAverage && (plan?.amountMode === "variable" || form.amountMode === "variable"));

  React.useEffect(() => {
    if (!open) return;
    setForm(recurringPlanDraft(plan, direction));
    setError("");
    setCategoryHistory(null);
    setHistoryError("");
    setHistoryLoading(false);
    setUseHistoricalAverage(false);
    dateTouchedRef.current = { startDate: false, firstDueDate: false, dueDay: false };
  }, [open, plan, direction]);

  React.useEffect(() => {
    let active = true;
    if (!open || isIncome || generated || !form.category || !form.currency) {
      setCategoryHistory(null);
      setHistoryError("");
      setHistoryLoading(false);
      if (!editing && useHistoricalAverage) {
        setUseHistoricalAverage(false);
        setForm((current) => ({ ...current, amountMode: "fixed" }));
      }
      return () => { active = false; };
    }

    setHistoryLoading(true);
    setHistoryError("");
    ownerApi.recurringFinanceCategoryHistory({
      category: form.category,
      direction: "expense",
      currency: form.currency,
      frequency: form.frequency,
    }).then((result) => {
      if (!active) return;
      const history = result?.history || null;
      setCategoryHistory(history);
      if (!editing && useHistoricalAverage && !(Number(history?.averageAmount || 0) > 0)) {
        setUseHistoricalAverage(false);
        setForm((current) => ({ ...current, amountMode: "fixed" }));
      }
      if (!editing && history?.count > 0) {
        setForm((current) => {
          const next = { ...current };
          if (!dateTouchedRef.current.startDate && history.firstPaidAt) next.startDate = dateInputValue(history.firstPaidAt);
          if (!dateTouchedRef.current.firstDueDate && history.suggestedNextDueAt) next.firstDueDate = dateInputValue(history.suggestedNextDueAt);
          if (current.frequency === "monthly" && !dateTouchedRef.current.dueDay && history.suggestedDueDay) next.dueDay = String(history.suggestedDueDay);
          return next;
        });
      }
    }).catch((err) => {
      if (!active) return;
      setCategoryHistory(null);
      setHistoryError(formatApiError(err, "No pude analizar el historial de esta categoría."));
    }).finally(() => {
      if (active) setHistoryLoading(false);
    });
    return () => { active = false; };
  }, [open, isIncome, generated, editing, form.category, form.currency, form.frequency, useHistoricalAverage]);

  React.useEffect(() => {
    if (!useHistoricalAverage) return;
    const average = Number(categoryHistory?.averageAmount || 0);
    if (!(average > 0)) return;
    setForm((current) => ({ ...current, amountMode: "variable", estimatedAmount: String(average) }));
  }, [useHistoricalAverage, categoryHistory?.averageAmount]);

  const patch = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const patchDate = (field) => (event) => {
    dateTouchedRef.current[field] = true;
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "firstDueDate" && current.frequency === "monthly" && value && !dateTouchedRef.current.dueDay
        ? { dueDay: String(Number(value.slice(-2))) }
        : {}),
    }));
  };
  const categoryKind = isIncome ? ["income", "both"] : ["expense", "both"];
  const compatibleCategories = categories.filter((row) => !row.archived && categoryKind.includes(row.kind));
  const compatibleAccounts = accounts.filter((row) => !row.archived && String(row.currency || "").toUpperCase() === String(form.currency || "").toUpperCase());
  const compatibleServices = services.filter((row) => (row.active !== false || idOf(row) === form.serviceId) && String(row.currency || "").toUpperCase() === String(form.currency || "").toUpperCase());
  const incomeRelationsChanged = isIncome && (!editing || form.clientId !== idOf(plan?.clientId) || form.serviceId !== idOf(plan?.serviceId));
  const historicalAverage = Number(categoryHistory?.averageAmount || 0);
  const historicalAverageAvailable = historicalAverage > 0;
  const averagePeriodLabel = RECURRING_FREQUENCY_LABELS[form.frequency]?.toLowerCase() || "período";

  const save = async () => {
    if (generated) return;
    if (!form.name.trim()) { setError(`Poné un nombre al ${isIncome ? "ingreso" : "gasto"} recurrente.`); return; }
    if (form.amountMode === "fixed" && !(Number(form.fixedAmount) > 0)) { setError("Indicá un monto fijo mayor a cero."); return; }
    if (form.amountMode === "variable" && !(Number(form.estimatedAmount) > 0)) { setError("Indicá un monto estimado mayor a cero."); return; }
    if (!form.startDate || !form.firstDueDate) { setError("Indicá inicio y primer vencimiento."); return; }
    if (isIncome && !structureLocked && incomeRelationsChanged && (!form.clientId || !form.serviceId)) { setError("Elegí cliente y servicio para el ingreso recurrente."); return; }

    setSaving(true); setError("");
    try {
      const payload = {
        name: form.name.trim(),
        amountMode: form.amountMode,
        fixedAmount: form.amountMode === "fixed" ? Number(form.fixedAmount) : null,
        estimatedAmount: form.amountMode === "variable" ? Number(form.estimatedAmount) : null,
        defaultAccountId: form.defaultAccountId || null,
        category: form.category.trim(),
        area: form.area.trim() || "Personal",
        endDate: form.endDate || null,
        notes: form.notes.trim(),
        ...(form.frequency === "monthly" ? { dueDay: Number(form.dueDay || 1) } : {}),
        ...(editing ? { status: form.status } : {}),
        // Historical plans keep their origin contract immutable. Future-facing fields
        // still remain editable and the backend freezes already-due expectations first.
        ...(!structureLocked ? {
          direction: isIncome ? "income" : "expense",
          currency: String(form.currency || "ARS").trim().toUpperCase(),
          frequency: form.frequency,
          startDate: form.startDate,
          firstDueDate: form.firstDueDate,
          ...(isIncome && incomeRelationsChanged ? { clientId: form.clientId || null, serviceId: form.serviceId || null } : {}),
        } : {}),
      };
      if (editing) await ownerApi.updateRecurringFinance(idOf(plan), payload);
      else await ownerApi.createRecurringFinance(payload);
      await onSaved?.();
      onClose?.();
    } catch (err) { setError(formatApiError(err, `No pude guardar el ${isIncome ? "ingreso" : "gasto"} recurrente.`)); }
    finally { setSaving(false); }
  };

  const title = generated
    ? `Ver ${isIncome ? "ingreso" : "gasto"} recurrente`
    : editing ? `Editar ${isIncome ? "ingreso" : "gasto"} recurrente` : `Nuevo ${isIncome ? "ingreso" : "gasto"} recurrente`;

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
    <DialogTitle sx={{ fontWeight: 1000 }}>{title}</DialogTitle>
    <DialogContent>
      <Stack spacing={1.05} sx={{ pt: .45 }}>
        {generated ? <Alert severity="info">Este recurrente nace de {plan?.origin?.label || "otro módulo"}. Economía lo muestra completo, pero su configuración se administra desde el módulo de origen para no desincronizar contratos, Billing o infraestructura.</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {structureLocked && !generated ? <Alert severity="warning">Este recurrente ya tiene historial. Podés cambiar monto futuro, cuenta, categoría, área, notas, día habitual y estado; moneda, frecuencia, cliente/servicio y fechas de origen quedan bloqueados para preservar lo registrado.</Alert> : null}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField fullWidth autoFocus={!editing} label="Nombre" value={form.name} disabled={generated} onChange={patch("name")} placeholder={isIncome ? "Ej. Mantenimiento mensual Cliente X" : "Ej. Alquiler oficina, internet, servidor"} />
          {allowVariable ? <TextField sx={{ minWidth: 150 }} select label="Tipo de monto" value={form.amountMode} disabled={generated || structureLocked} onChange={patch("amountMode")}><MenuItem value="fixed">Fijo</MenuItem><MenuItem value="variable">Variable / estimado</MenuItem></TextField> : null}
          {form.amountMode === "fixed"
            ? <TextField fullWidth type="number" label="Monto fijo" value={form.fixedAmount} disabled={generated} onChange={patch("fixedAmount")} />
            : <TextField fullWidth type="number" label={useHistoricalAverage ? "Promedio histórico" : "Monto estimado"} value={form.estimatedAmount} disabled={generated || useHistoricalAverage} onChange={patch("estimatedAmount")} />}
          <TextField sx={{ minWidth: 125 }} label="Moneda" value={form.currency} disabled={generated || structureLocked} onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value.toUpperCase(), defaultAccountId: "", serviceId: isIncome ? "" : current.serviceId }))} />
        </Stack>
        {isIncome ? <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField fullWidth select label="Cliente" value={form.clientId} disabled={generated || structureLocked} onChange={patch("clientId")}><MenuItem value="">Elegir cliente</MenuItem>{clients.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name || row.company || "Cliente"}</MenuItem>)}</TextField>
          <TextField fullWidth select label="Servicio" value={form.serviceId} disabled={generated || structureLocked} onChange={patch("serviceId")}><MenuItem value="">Elegir servicio</MenuItem>{compatibleServices.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name} · {row.currency}</MenuItem>)}</TextField>
        </Stack> : null}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField fullWidth select label="Frecuencia" value={form.frequency} disabled={generated || structureLocked} onChange={patch("frequency")}>{Object.entries(RECURRING_FREQUENCY_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
          <TextField fullWidth type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={form.startDate} disabled={generated || structureLocked} onChange={patchDate("startDate")} helperText={!editing && categoryHistory?.firstPaidAt ? `Detectado: pagás esta categoría desde ${shortDate(categoryHistory.firstPaidAt)}` : ""} />
          <TextField fullWidth type="date" label="Primer vencimiento" InputLabelProps={{ shrink: true }} value={form.firstDueDate} disabled={generated || structureLocked} onChange={patchDate("firstDueDate")} helperText={!editing && categoryHistory?.suggestedNextDueAt ? `Próximo sugerido: ${shortDate(categoryHistory.suggestedNextDueAt)}` : ""} />
          {form.frequency === "monthly" ? <TextField sx={{ minWidth: 115 }} type="number" label="Día habitual" value={form.dueDay} disabled={generated} inputProps={{ min: 1, max: 31 }} onChange={(event) => { dateTouchedRef.current.dueDay = true; setForm((current) => ({ ...current, dueDay: event.target.value })); }} /> : null}
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField fullWidth select label={isIncome ? "Cuenta de cobro prevista" : "Cuenta de pago prevista"} value={form.defaultAccountId} disabled={generated} onChange={patch("defaultAccountId")}><MenuItem value="">Sin cuenta fija</MenuItem>{compatibleAccounts.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name} · {row.currency}</MenuItem>)}</TextField>
          <TextField fullWidth select label="Categoría" value={form.category} disabled={generated} onChange={patch("category")}><MenuItem value="">Sin categoría</MenuItem>{compatibleCategories.map((row) => <MenuItem key={`${row.kind}:${idOf(row)}`} value={row.name}>{row.name}</MenuItem>)}</TextField>
          <TextField fullWidth label="Área" value={form.area} disabled={generated} onChange={patch("area")} placeholder="Personal, WebProfit, Casa…" />
        </Stack>
        {!isIncome && form.category ? <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ px: .15 }}>
          <Box sx={{ minWidth: 0 }}>
            {historyLoading ? <Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Mirando pagos anteriores de “{form.category}”…</Typography> : historyError ? <Typography sx={{ color: "warning.main", fontSize: ".72rem" }}>{historyError}</Typography> : categoryHistory?.count > 0 ? <Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Historial: desde <strong>{shortDate(categoryHistory.firstPaidAt)}</strong> · último {shortDate(categoryHistory.lastPaidAt)} · {categoryHistory.count} movimiento(s) en {categoryHistory.periodsCount} período(s) · promedio {averagePeriodLabel} <strong>{money(historicalAverage, form.currency)}</strong></Typography> : <Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>No encontré gastos anteriores en {form.currency} para esta categoría. Las fechas y el monto quedan manuales.</Typography>}
          </Box>
          <FormControlLabel
            sx={{ m: 0, whiteSpace: "nowrap" }}
            control={<Switch size="small" checked={useHistoricalAverage} disabled={generated || historyLoading || !historicalAverageAvailable || (structureLocked && plan?.amountMode !== "variable")} onChange={(event) => { const checked = event.target.checked; setUseHistoricalAverage(checked); if (!checked) setForm((current) => ({ ...current, amountMode: "fixed" })); }} />}
            label={<Typography sx={{ fontSize: ".72rem", fontWeight: 900 }}>Usar promedio</Typography>}
          />
        </Stack> : null}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField fullWidth type="date" label="Finaliza (opcional)" InputLabelProps={{ shrink: true }} value={form.endDate} disabled={generated} onChange={patch("endDate")} />
          {editing ? <TextField fullWidth select label="Estado" value={form.status} disabled={generated} onChange={patch("status")}>{Object.entries(RECURRING_STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField> : null}
        </Stack>
        <TextField multiline minRows={3} label="Notas / contexto operativo" value={form.notes} disabled={generated} onChange={patch("notes")} helperText="Proveedor, condiciones, excepciones o contexto. Esta nota queda preparada para futuras propuestas desde IA; la IA no escribe automáticamente en esta etapa." />
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Cerrar</Button>{!generated ? <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear recurrente"}</Button> : null}</DialogActions>
  </Dialog>;
}

function RecurringDeleteDialog({ plan, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => { if (open) setError(""); }, [open, plan]);
  if (!plan) return null;
  const hasHistory = Boolean(plan.hasHistory);
  const generated = Boolean(plan.origin?.generated);
  const submit = async () => {
    if (generated) return;
    setDeleting(true); setError("");
    try { await ownerApi.deleteRecurringFinance(idOf(plan)); await onDeleted?.(); onClose?.(); }
    catch (err) { setError(formatApiError(err, "No pude eliminar este recurrente.")); }
    finally { setDeleting(false); }
  };
  return <Dialog open={open} onClose={deleting ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 1000 }}>Eliminar recurrente</DialogTitle>
    <DialogContent><Stack spacing={1} sx={{ pt: .35 }}>{error ? <Alert severity="error">{error}</Alert> : null}<Typography sx={{ fontSize: ".82rem" }}>¿Eliminar <strong>{plan.name}</strong> de Economía?</Typography>{hasHistory ? <Alert severity="warning">Tiene historial. Se quitará de la economía futura, pero los movimientos y períodos ya registrados se conservarán para no romper balances ni trazabilidad.</Alert> : <Alert severity="info">Todavía no tiene historial: se eliminará definitivamente la configuración.</Alert>}</Stack></DialogContent>
    <DialogActions><Button onClick={onClose} disabled={deleting}>Cancelar</Button><Button color="error" variant="contained" onClick={submit} disabled={deleting || generated}>{deleting ? "Eliminando…" : "Eliminar"}</Button></DialogActions>
  </Dialog>;
}

function EconomyImportDialog({ open, preview, importing, onClose, onConfirm }) {
  if (!preview) return null;
  return <Dialog open={open} onClose={importing ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 1000 }}>Importar JSON de Economía</DialogTitle>
    <DialogContent>
      <Stack spacing={1} sx={{ pt: .35 }}>
        <Alert severity="info">Sólo se importan recurrentes manuales. Billing WebProfit, contratos e infraestructura se omiten porque se regeneran desde su módulo de origen.</Alert>
        <Typography sx={{ fontSize: ".78rem" }}><strong>{preview.fileName}</strong></Typography>
        <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap">
          <Chip size="small" color="success" label={`${preview.toCreate.length} para crear`} />
          <Chip size="small" variant="outlined" label={`${preview.duplicates.length} duplicados omitidos`} />
          <Chip size="small" variant="outlined" label={`${preview.generatedSkipped} generados omitidos`} />
          <Chip size="small" color={preview.invalid.length ? "warning" : "default"} variant="outlined" label={`${preview.invalid.length} inválidos`} />
        </Stack>
        {preview.invalid.length ? <Alert severity="warning">{preview.invalid.slice(0, 4).map((row) => `${row.name}: ${row.reasons.join(", ")}`).join(" · ")}{preview.invalid.length > 4 ? " · …" : ""}</Alert> : null}
        {!preview.toCreate.length ? <Alert severity="warning">No hay registros nuevos válidos para importar.</Alert> : null}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={importing}>Cancelar</Button>
      <Button variant="contained" onClick={onConfirm} disabled={importing || !preview.toCreate.length}>{importing ? "Importando…" : `Importar ${preview.toCreate.length}`}</Button>
    </DialogActions>
  </Dialog>;
}

function EconomyView({ economy, loading, accounts, categories, clients, services, onReload }) {
  const navigate = useNavigate();
  const [section, setSection] = React.useState("expenses");
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState("");
  const [deleting, setDeleting] = React.useState(null);
  const [filters, setFilters] = React.useState({ q: "", status: "active", currency: "", category: "" });
  const [actionId, setActionId] = React.useState("");
  const [localError, setLocalError] = React.useState("");
  const [importPreview, setImportPreview] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const importInputRef = React.useRef(null);
  const plans = economy?.plans || [];
  const fixedExpenses = plans.filter((row) => row.direction === "expense" && row.amountMode === "fixed");
  // OWNER_ECONOMY_BILLING_FIRST_PAYMENT_HOTFIX: a Billing WebProfit mirror is
  // diagnostic until the provider has recorded the first successful payment.
  // It remains visible under “Todos” but does not inflate recurring income.
  const pendingFirstPayment = plans.filter((row) => row.direction === "income" && row.activationState === "waiting_first_payment");
  const consolidatedBillingDuplicates = plans.filter((row) => row.direction === "income" && row.activationState === "duplicate_billing_account");
  const recurringIncomes = plans.filter((row) => row.direction === "income" && row.economyEligible !== false);
  const sourceRows = section === "expenses" ? fixedExpenses : section === "income" ? recurringIncomes : plans;
  const categoryNames = [...new Set(sourceRows.map((row) => row.category).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const currencies = [...new Set(sourceRows.map((row) => row.currency).filter(Boolean))].sort();
  const visible = sourceRows.filter((row) => {
    const q = filters.q.trim().toLowerCase();
    const clientName = row.clientId?.name || row.clientId?.company || "";
    const serviceName = row.serviceId?.name || "";
    if (q && ![row.name, row.category, row.area, row.notes, row.origin?.label, clientName, serviceName].some((value) => String(value || "").toLowerCase().includes(q))) return false;
    if (filters.status && String(row.effectiveStatus || row.status) !== filters.status) return false;
    if (filters.currency && row.currency !== filters.currency) return false;
    if (filters.category && row.category !== filters.category) return false;
    return true;
  });
  const activeExpenses = fixedExpenses.filter((row) => row.effectiveStatus === "active");
  const pausedExpenses = fixedExpenses.filter((row) => row.effectiveStatus === "paused");
  const overdueExpenses = activeExpenses.filter((row) => row.nextOccurrence?.dueState === "overdue");
  const activeIncome = recurringIncomes.filter((row) => row.effectiveStatus === "active");
  const next30ExpenseByCurrency = activeExpenses.reduce((acc, row) => { const currency = row.currency || "ARS"; acc[currency] = (acc[currency] || 0) + Number(row.next30DaysExpectedAmount || 0); return acc; }, {});
  const next30IncomeByCurrency = activeIncome.reduce((acc, row) => { const currency = row.currency || "ARS"; acc[currency] = (acc[currency] || 0) + Number(row.next30DaysExpectedAmount || 0); return acc; }, {});

  React.useEffect(() => { setFilters((current) => ({ ...current, currency: "", category: "" })); }, [section]);

  const togglePause = async (row) => {
    if (row.origin?.generated) return;
    setActionId(idOf(row)); setLocalError("");
    try { await ownerApi.updateRecurringFinance(idOf(row), { status: row.effectiveStatus === "paused" ? "active" : "paused" }); await onReload?.(); }
    catch (err) { setLocalError(formatApiError(err, "No pude cambiar el estado del recurrente.")); }
    finally { setActionId(""); }
  };

  const exportEconomyJson = () => {
    const manualPlans = plans.filter((row) => !row.origin?.generated).map((row) => economyPortablePlan(row, { diagnostic: true }));
    const generatedPlans = plans.filter((row) => row.origin?.generated).map((row) => economyPortablePlan(row, { diagnostic: true }));
    const payload = {
      schema: "webprofit.owner.economy",
      version: 1,
      exportedAt: new Date().toISOString(),
      importPolicy: "manual-only; generated Billing/contracts/infrastructure are diagnostic and must be regenerated at origin",
      manualPlans,
      generatedPlans,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `webprofit-economia-${dateInputValue()}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
  };

  const readEconomyJson = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setLocalError("");
    try {
      const raw = JSON.parse(await file.text());
      const normalized = normalizeEconomyImport(raw);
      const existing = new Set(plans.map((row) => economyPlanKey(row)));
      const seen = new Set();
      const duplicates = [];
      const toCreate = [];
      normalized.valid.forEach((row) => {
        const key = economyPlanKey(row);
        if (existing.has(key) || seen.has(key)) duplicates.push(row);
        else { seen.add(key); toCreate.push(row); }
      });
      setImportPreview({ fileName: file.name, ...normalized, duplicates, toCreate });
    } catch (err) {
      setLocalError(err instanceof SyntaxError ? "El archivo no contiene un JSON válido." : formatApiError(err, "No pude leer el JSON de Economía."));
    }
  };

  const importEconomyJson = async () => {
    if (!importPreview?.toCreate?.length) return;
    setImporting(true); setLocalError("");
    const failures = [];
    let created = 0;
    try {
      for (const row of importPreview.toCreate) {
        try {
          const createdResponse = await ownerApi.createRecurringFinance({
            name: row.name,
            direction: row.direction,
            amountMode: row.amountMode,
            fixedAmount: row.fixedAmount,
            estimatedAmount: row.estimatedAmount,
            currency: row.currency,
            defaultAccountId: row.defaultAccountId || null,
            clientId: row.direction === "income" ? row.clientId : undefined,
            serviceId: row.direction === "income" ? row.serviceId : undefined,
            category: row.category,
            area: row.area || "Personal",
            frequency: row.frequency,
            startDate: row.startDate,
            firstDueDate: row.firstDueDate,
            dueDay: row.dueDay,
            endDate: row.endDate || null,
            notes: row.notes,
          });
          const createdPlan = createdResponse?.plan;
          if (createdPlan && ["paused", "ended"].includes(row.status)) {
            await ownerApi.updateRecurringFinance(idOf(createdPlan), { status: row.status });
          }
          created += 1;
        } catch (err) {
          failures.push(`${row.name}: ${formatApiError(err, "error de importación")}`);
        }
      }
      await onReload?.();
      setImportPreview(null);
      if (failures.length) setLocalError(`Importados ${created}. Fallaron ${failures.length}: ${failures.slice(0, 3).join(" · ")}${failures.length > 3 ? " · …" : ""}`);
    } finally { setImporting(false); }
  };

  const sectionTitle = section === "expenses" ? "Gastos fijos" : section === "income" ? "Ingresos recurrentes" : "Todos los recurrentes";
  const sectionHelper = section === "expenses" ? "Obligaciones repetitivas que forman tu costo base." : section === "income" ? "Cobros esperados repetitivos, manuales o derivados de contratos." : "Vista completa de la economía recurrente, incluidos variables y planes generados.";

  return <Stack spacing={.7}>
    <Alert severity="info" icon={<EventRepeatRoundedIcon fontSize="inherit" />}>Economía configura lo que debería pasar; Movimientos registra lo que efectivamente pasó. Los recurrentes comparten un solo motor para proyección, calendario, estadísticas y futuras propuestas de IA.</Alert>
    {pendingFirstPayment.length ? <Alert severity="warning">{pendingFirstPayment.length} Billing WebProfit están configurados pero todavía no tuvieron su primer pago. No se contabilizan como ingresos recurrentes ni en proyecciones; podés inspeccionarlos en “Todos los recurrentes”.</Alert> : null}
    {consolidatedBillingDuplicates.length ? <Alert severity="info">{consolidatedBillingDuplicates.length} Billing WebProfit quedaron consolidados porque pertenecen a una misma cuenta/persona con más de un proyecto. Se muestran en “Todos”, pero Economía suma una sola mensualidad por cuenta.</Alert> : null}
    {localError ? <Alert severity="error" onClose={() => setLocalError("")}>{localError}</Alert> : null}

    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" }, gap: .65 }}>
      <StatCard label="GASTOS FIJOS ACTIVOS" value={activeExpenses.length} helper={`${pausedExpenses.length} pausados · ${overdueExpenses.length} vencidos`} />
      <Box sx={{ ...surfaceSx, p: 1.05 }}><Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950 }}>GASTOS · PRÓXIMOS 30 DÍAS</Typography><Stack direction="row" spacing={.35} useFlexGap flexWrap="wrap" sx={{ mt: .45 }}>{Object.entries(next30ExpenseByCurrency).length ? Object.entries(next30ExpenseByCurrency).map(([currency, value]) => <Chip key={currency} size="small" label={`${currency} ${money(value, currency)}`} />) : <Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>Sin vencimientos proyectados.</Typography>}</Stack></Box>
      <StatCard label="INGRESOS RECURRENTES ACTIVOS" value={activeIncome.length} helper={`${recurringIncomes.length} configurados en total`} />
      <Box sx={{ ...surfaceSx, p: 1.05 }}><Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950 }}>INGRESOS · PRÓXIMOS 30 DÍAS</Typography><Stack direction="row" spacing={.35} useFlexGap flexWrap="wrap" sx={{ mt: .45 }}>{Object.entries(next30IncomeByCurrency).length ? Object.entries(next30IncomeByCurrency).map(([currency, value]) => <Chip key={currency} size="small" label={`${currency} ${money(value, currency)}`} />) : <Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>Sin cobros proyectados.</Typography>}</Stack></Box>
    </Box>

    <Box sx={{ ...surfaceSx, overflow: "hidden" }}>
      <Tabs value={section} onChange={(_event, value) => setSection(value)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 950, fontSize: ".7rem" } }}>
        <Tab value="expenses" label={`Gastos fijos (${fixedExpenses.length})`} />
        <Tab value="income" label={`Ingresos recurrentes (${recurringIncomes.length})`} />
        <Tab value="all" label={`Todos (${plans.length})`} />
      </Tabs>
    </Box>

    <Box sx={{ ...surfaceSx, p: .85 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={.7} alignItems={{ md: "center" }}>
        <Box><Typography sx={{ fontWeight: 1000, fontSize: ".86rem" }}>{sectionTitle}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".62rem" }}>{sectionHelper}</Typography></Box>
        <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap">
          <input ref={importInputRef} hidden type="file" accept="application/json,.json" onChange={readEconomyJson} />
          <Button variant="outlined" color="inherit" startIcon={<FileUploadRoundedIcon />} onClick={() => importInputRef.current?.click()} sx={{ textTransform: "none", fontWeight: 950 }}>Importar JSON</Button>
          <Button variant="outlined" color="inherit" startIcon={<FileDownloadRoundedIcon />} onClick={exportEconomyJson} disabled={!plans.length} sx={{ textTransform: "none", fontWeight: 950 }}>Exportar JSON</Button>
          {(section === "expenses" || section === "all") ? <Button variant={section === "expenses" ? "contained" : "outlined"} startIcon={<AddRoundedIcon />} onClick={() => setCreating("expense")} sx={{ textTransform: "none", fontWeight: 950 }}>Gasto fijo</Button> : null}
          {(section === "income" || section === "all") ? <Button variant={section === "income" ? "contained" : "outlined"} startIcon={<AddRoundedIcon />} onClick={() => setCreating("income")} sx={{ textTransform: "none", fontWeight: 950 }}>Ingreso recurrente</Button> : null}
        </Stack>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" }, gap: .55, mt: .75 }}>
        {[{ title: "1. Gastos fijos", detail: "ABM completo con borrado seguro y proyección.", state: "Operativo" }, { title: "2. Ingresos recurrentes", detail: "Vista, alta, edición, pausa y borrado de cobros esperados.", state: "Operativo" }, { title: "3. Metas y reservas", detail: "Colchón, ahorro y compromisos sin mezclarlos con caja real.", state: "Siguiente" }, { title: "4. IA financiera", detail: "Propuestas desde lenguaje natural con confirmación y trazabilidad.", state: "Base lista" }].map((row) => <Box key={row.title} sx={{ p: .7, border: "1px solid", borderColor: "divider", borderRadius: 1.7 }}><Stack direction="row" justifyContent="space-between" gap={.5}><Typography sx={{ fontWeight: 950, fontSize: ".7rem" }}>{row.title}</Typography><Chip size="small" variant="outlined" label={row.state} sx={{ height: 20, fontSize: ".55rem" }} /></Stack><Typography sx={{ color: "text.secondary", fontSize: ".6rem", mt: .28 }}>{row.detail}</Typography></Box>)}
      </Box>
    </Box>

    <Box sx={{ ...surfaceSx, p: .8 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={.55} alignItems={{ lg: "center" }}>
        <TextField size="small" fullWidth placeholder="Buscar nombre, cliente, servicio, categoría, área o nota…" value={filters.q} onChange={(event) => setFilters({ ...filters, q: event.target.value })} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment> }} />
        <TextField size="small" select label="Estado" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })} sx={{ minWidth: 130 }}><MenuItem value="">Todos</MenuItem>{Object.entries(RECURRING_STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
        <TextField size="small" select label="Moneda" value={filters.currency} onChange={(event) => setFilters({ ...filters, currency: event.target.value })} sx={{ minWidth: 125 }}><MenuItem value="">Todas</MenuItem>{currencies.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
        <TextField size="small" select label="Categoría" value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })} sx={{ minWidth: 150 }}><MenuItem value="">Todas</MenuItem>{categoryNames.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
      </Stack>
    </Box>

    {loading ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={28} /></Stack> : <Stack spacing={.45}>{visible.map((row) => {
      const generated = Boolean(row.origin?.generated);
      const account = accounts.find((item) => idOf(item) === idOf(row.defaultAccountId));
      const status = row.effectiveStatus || row.status;
      const clientName = row.clientId?.name || row.clientId?.company || "";
      const serviceName = row.serviceId?.name || "";
      return <Box key={idOf(row)} sx={{ ...surfaceSx, p: .8 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={.75} alignItems={{ md: "center" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={.4} useFlexGap flexWrap="wrap" alignItems="center"><Typography sx={{ fontWeight: 1000, fontSize: ".78rem" }}>{row.name}</Typography>{section === "all" ? <Chip size="small" variant="outlined" label={row.direction === "income" ? "Ingreso" : "Gasto"} sx={{ height: 20, fontSize: ".55rem" }} /> : null}<Chip size="small" label={RECURRING_STATUS_LABELS[status] || status} color={status === "active" ? "success" : "default"} variant={status === "active" ? "filled" : "outlined"} sx={{ height: 20, fontSize: ".55rem" }} />{row.activationState === "waiting_first_payment" ? <Chip size="small" color="warning" label="Espera 1er pago" sx={{ height: 20, fontSize: ".55rem" }} /> : null}{row.activationState === "duplicate_billing_account" ? <Chip size="small" color="info" variant="outlined" label="Misma cuenta · agrupado" sx={{ height: 20, fontSize: ".55rem" }} /> : null}{row.billingGroup?.amountConflict && row.billingGroupRole === "canonical" ? <Chip size="small" color="warning" variant="outlined" label="Importes distintos" sx={{ height: 20, fontSize: ".55rem" }} /> : null}{row.amountMode === "variable" ? <Chip size="small" variant="outlined" label="Variable" sx={{ height: 20, fontSize: ".55rem" }} /> : null}{generated ? <Chip size="small" variant="outlined" label={row.origin?.label || "Generado"} sx={{ height: 20, fontSize: ".55rem" }} /> : null}{row.nextOccurrence?.dueState === "overdue" && row.economyEligible !== false ? <Chip size="small" color="error" label="Vencido" sx={{ height: 20, fontSize: ".55rem" }} /> : null}</Stack>
            <Typography sx={{ color: "text.secondary", fontSize: ".61rem", mt: .25 }}>{RECURRING_FREQUENCY_LABELS[row.frequency] || row.frequency}{row.category ? ` · ${row.category}` : ""}{row.area ? ` · ${row.area}` : ""}{account ? ` · ${account.name}` : ""}</Typography>
            {(clientName || serviceName) ? <Typography sx={{ color: "text.secondary", fontSize: ".59rem", mt: .16 }}>{clientName ? `Cliente: ${clientName}` : ""}{clientName && serviceName ? " · " : ""}{serviceName ? `Servicio: ${serviceName}` : ""}</Typography> : null}
            {row.billingGroup?.count > 1 ? <Typography sx={{ color: row.billingGroup?.amountConflict ? "warning.main" : "text.secondary", fontSize: ".59rem", mt: .16, fontWeight: row.billingGroup?.amountConflict ? 900 : 500 }}>Cuenta WebProfit compartida por {row.billingGroup.count} recurrentes{row.billingGroup.projectNames?.length ? ` · ${row.billingGroup.projectNames.join(" + ")}` : ""}{row.billingGroup.amountConflict ? " · revisar precio Billing" : ""}</Typography> : null}
            {generated && row.origin?.projectId ? <Typography sx={{ color: "text.secondary", fontSize: ".59rem", mt: .16 }}>Origen comercial: Proyecto · precio y módulos se administran en Proyecto 360.</Typography> : null}
            {row.notes ? <Typography sx={{ color: "text.secondary", fontSize: ".59rem", mt: .18 }} noWrap>Nota: {row.notes}</Typography> : null}
          </Box>
          <Box sx={{ minWidth: { md: 180 }, textAlign: { md: "right" } }}><Typography sx={{ fontWeight: 1000, fontSize: ".86rem" }}>{money(recurringPlanAmount(row), row.currency)}</Typography><Typography sx={{ color: row.economyEligible === false ? (row.activationState === "duplicate_billing_account" ? "info.main" : "warning.main") : "text.secondary", fontSize: ".59rem", fontWeight: row.economyEligible === false ? 900 : 400 }}>{row.activationState === "duplicate_billing_account" ? `Incluido en ${row.billingGroup?.canonicalName || "la cuenta principal"}` : row.activationState === "waiting_first_payment" ? "No computa hasta 1er pago" : row.nextDueAt ? `Próximo: ${shortDate(row.nextDueAt)}` : "Sin próximo vencimiento"}</Typography></Box>
          <Stack direction="row" spacing={.35} useFlexGap flexWrap="wrap">{row.origin?.projectId ? <Button size="small" color="inherit" variant="outlined" startIcon={<OpenInNewRoundedIcon />} onClick={() => navigate(`/dashboard/owner/proyectos/${encodeURIComponent(row.origin.projectId)}`)} sx={{ textTransform: "none", fontWeight: 900 }}>Proyecto</Button> : null}<Button size="small" variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => setEditing(row)} sx={{ textTransform: "none", fontWeight: 900 }}>{generated ? "Ver" : "Editar"}</Button>{!generated && status !== "ended" ? <Button size="small" color="inherit" disabled={actionId === idOf(row)} onClick={() => togglePause(row)} sx={{ textTransform: "none", fontWeight: 900 }}>{status === "paused" ? "Reactivar" : "Pausar"}</Button> : null}{!generated ? <Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} disabled={actionId === idOf(row)} onClick={() => setDeleting(row)} sx={{ textTransform: "none", fontWeight: 900 }}>Eliminar</Button> : null}</Stack>
        </Stack>
      </Box>;
    })}{!visible.length ? <Box sx={{ ...surfaceSx, p: 2, textAlign: "center" }}><Typography sx={{ fontWeight: 950 }}>No hay recurrentes con estos filtros.</Typography><Typography sx={{ color: "text.secondary", fontSize: ".68rem", mt: .25 }}>{section === "income" ? "Creá un ingreso recurrente para empezar a proyectar cobros." : "Podés crear la configuración desde Economía o revisar otro estado."}</Typography></Box> : null}</Stack>}

    <EconomyImportDialog open={Boolean(importPreview)} preview={importPreview} importing={importing} onClose={() => setImportPreview(null)} onConfirm={importEconomyJson} />
    <RecurringPlanDialog open={Boolean(creating)} plan={null} direction={creating || "expense"} accounts={accounts} categories={categories} clients={clients} services={services} onClose={() => setCreating("")} onSaved={onReload} />
    <RecurringPlanDialog open={Boolean(editing)} plan={editing} direction={editing?.direction || "expense"} accounts={accounts} categories={categories} clients={clients} services={services} onClose={() => setEditing(null)} onSaved={onReload} />
    <RecurringDeleteDialog open={Boolean(deleting)} plan={deleting} onClose={() => setDeleting(null)} onDeleted={onReload} />
  </Stack>;
}


export default function OwnerFinancePage() {
  const [tab, setTab] = React.useState("overview");
  const [accounts, setAccounts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [detectedCategories, setDetectedCategories] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [transactions, setTransactions] = React.useState([]);
  const [recentTransactions, setRecentTransactions] = React.useState([]);
  const movementRequestId = React.useRef(0);
  const [pagination, setPagination] = React.useState({ page: 1, pages: 1, total: 0, hasMore: false });
  const [loadingBase, setLoadingBase] = React.useState(true);
  const [loadingMovements, setLoadingMovements] = React.useState(false);
  const [exportingAudit, setExportingAudit] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dialog, setDialog] = React.useState("");
  const [editingMovement, setEditingMovement] = React.useState(null);
  const [editingCategory, setEditingCategory] = React.useState(null);
  const [filters, setFilters] = React.useState({ q: "", type: "", category: "", accountId: "", sourceKind: "", sortBy: "createdAt", from: "", to: "" });
  const [draftFilters, setDraftFilters] = React.useState(filters);
  const [stats, setStats] = React.useState(null);
  const [statsLoading, setStatsLoading] = React.useState(false);
  const [statsFilter, setStatsFilter] = React.useState({ start: startOfMonthValue(), end: dateInputValue(), bucket: "day", currency: "ARS", accountId: "", category: "" });
  const [economy, setEconomy] = React.useState(null);
  const [economyLoading, setEconomyLoading] = React.useState(false);

  const loadBase = React.useCallback(async () => {
    setLoadingBase(true); setError("");
    try {
      const [a, cats, c, s, p] = await Promise.all([
        ownerApi.listFinanceAccounts(), ownerApi.listFinanceCategories(), ownerApi.listClients({ limit: 250 }), ownerApi.listServices({ includeInactive: true }), ownerApi.listInitiatives({ status: "active", limit: 150 }),
      ]);
      setAccounts(a?.accounts || []); setCategories(cats?.categories || []); setDetectedCategories((cats?.detected || []).map((row, index) => ({ ...row, id: `detected-${index}`, detected: true }))); setClients(c?.clients || []); setServices(s?.services || []); setProjects(p?.initiatives || []);
    } catch (err) { setError(formatApiError(err, "No pude cargar la configuración de Dinero.")); }
    finally { setLoadingBase(false); }
  }, []);

  const loadMovements = React.useCallback(async (nextPage = 1, nextFilters = filters) => {
    const requestId = ++movementRequestId.current;
    setLoadingMovements(true); setError("");
    try {
      const result = await ownerApi.listFinanceTransactions({ ...nextFilters, page: nextPage, limit: 50 });
      if (requestId !== movementRequestId.current) return;
      setTransactions(result?.transactions || []);
      setPagination(result?.pagination || { page: nextPage, pages: 1, total: (result?.transactions || []).length, hasMore: false });
    } catch (err) {
      if (requestId === movementRequestId.current) setError(formatApiError(err, "No pude cargar los movimientos."));
    } finally {
      if (requestId === movementRequestId.current) setLoadingMovements(false);
    }
  }, [filters]);

  // "Últimos movimientos" uses the ordinary ledger endpoint, independent of screen filters.
  // A bot-created entry is the same normal transaction as a manually entered one.
  const loadRecent = React.useCallback(async () => {
    try {
      const result = await ownerApi.listFinanceTransactions({ page: 1, limit: 8, sortBy: "createdAt" });
      setRecentTransactions(result?.transactions || []);
    } catch (err) { setError(formatApiError(err, "No pude actualizar los últimos movimientos.")); }
  }, []);

  const loadStats = React.useCallback(async (next = statsFilter) => {
    setStatsLoading(true); setError("");
    try { const result = await ownerFinanceStatsApi.history(next); setStats(result?.stats || null); }
    catch (err) { setError(formatApiError(err, "No pude cargar las estadísticas.")); }
    finally { setStatsLoading(false); }
  }, [statsFilter]);

  const loadEconomy = React.useCallback(async () => {
    setEconomyLoading(true); setError("");
    try { const result = await ownerApi.listRecurringFinance({ includeEnded: true }); setEconomy(result?.recurring || { plans: [], summary: {} }); }
    catch (err) { setError(formatApiError(err, "No pude cargar la estructura de Economía.")); }
    finally { setEconomyLoading(false); }
  }, []);

  React.useEffect(() => { loadBase(); }, [loadBase]);
  React.useEffect(() => {
    loadMovements(1, filters);
    loadRecent();
  }, []); // Initial load. Filters are applied explicitly; recent records are unfiltered.
  React.useEffect(() => {
    let lastRefresh = 0;
    const refreshWhenVisible = () => {
      if (document.visibilityState === "hidden" || Date.now() - lastRefresh < 1000) return;
      lastRefresh = Date.now();
      loadRecent();
      loadMovements(tab === "movements" ? pagination.page || 1 : 1, filters);
    };
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    // Updates arrive from WhatsApp without navigating this browser tab.
    // Check while this page is visible, without creating a second kind of movement.
    const interval = window.setInterval(() => {
      if (tab === "overview" || (tab === "movements" && pagination.page === 1)) refreshWhenVisible();
    }, 30000);
    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.clearInterval(interval);
    };
  }, [tab, pagination.page, filters, loadRecent, loadMovements]);

  React.useEffect(() => {
    const currencies = [...new Set(accounts.map((row) => row.currency).filter(Boolean))];
    if (currencies.length && !currencies.includes(statsFilter.currency)) {
      setStatsFilter((current) => ({ ...current, currency: currencies[0] }));
      setStats(null);
    }
  }, [accounts, statsFilter.currency]);
  React.useEffect(() => { if (tab === "stats" && !stats) loadStats(statsFilter); }, [tab]);
  React.useEffect(() => { if ((tab === "economy" || tab === "stats") && !economy) loadEconomy(); }, [tab, economy, loadEconomy]);

  const refreshAll = React.useCallback(async () => {
    await Promise.all([loadBase(), loadMovements(pagination.page || 1, filters), loadRecent()]);
    if (stats) await loadStats(statsFilter);
    if (economy) await loadEconomy();
  }, [loadBase, loadMovements, loadRecent, loadStats, loadEconomy, pagination.page, filters, stats, statsFilter, economy]);

  const applyFilters = () => { setFilters(draftFilters); loadMovements(1, draftFilters); };
  const clearFilters = () => { const clean = { q: "", type: "", category: "", accountId: "", sourceKind: "", sortBy: "createdAt", from: "", to: "" }; setDraftFilters(clean); setFilters(clean); loadMovements(1, clean); };
  const exportMoney = async (format = "json", importedOnly = false) => {
    setExportingAudit(true); setError("");
    try {
      const result = await ownerApi.exportOwnerMoneyAudit({ format, ...(importedOnly ? { sourceKind: "import" } : {}) });
      if (format === "json" && (!result?.ok || !result?.coverage?.complete)) throw new Error("La exportación no está completa.");
      const content = format === "json" ? JSON.stringify(result, null, 2) : String(result);
      const blob = new Blob([content], { type: format === "json" ? "application/json;charset=utf-8" : "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `owner-dinero-${importedOnly ? "importados" : "auditoria"}-${dateInputValue()}-${Date.now()}.${format}`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(formatApiError(err, "No pude generar la exportación de Dinero.")); }
    finally { setExportingAudit(false); }
  };

  const accountCurrencies = [...new Set(accounts.filter((row) => !row.archived).map((row) => row.currency))];
  const totalBalanceByCurrency = accountCurrencies.map((currency) => ({ currency, total: accounts.filter((row) => !row.archived && row.currency === currency).reduce((sum, row) => sum + Number(row.balance || 0), 0) }));
  const latest = recentTransactions;
  const categoryOptions = [...categories, ...detectedCategories];

  return <Box sx={{ width: "100%", maxWidth: 1320, mx: "auto" }}>
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={.7} alignItems={{ md: "center" }} sx={{ mb: .65 }}>
      <Box><Typography sx={{ fontSize: "1.08rem", fontWeight: 1000 }}>Dinero</Typography><Typography sx={{ color: "text.secondary", fontSize: ".67rem" }}>Cuentas, movimientos, economía proyectada, categorías y lectura financiera del Owner en una sola superficie.</Typography></Box>
      <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={() => exportMoney("json")} disabled={exportingAudit} sx={{ textTransform: "none", fontWeight: 900 }}>{exportingAudit ? "Exportando…" : "Auditoría JSON"}</Button>
        <Button size="small" variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={() => exportMoney("csv")} disabled={exportingAudit} sx={{ textTransform: "none", fontWeight: 900 }}>Movimientos CSV</Button>
        <Button size="small" color="inherit" variant="outlined" onClick={() => { loadRecent(); loadMovements(tab === "movements" ? pagination.page || 1 : 1, filters); }} disabled={loadingMovements} sx={{ textTransform: "none", fontWeight: 900 }}>Actualizar</Button>
        <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setDialog("account")} sx={{ textTransform: "none", fontWeight: 900 }}>Cuenta</Button><Button size="small" variant="contained" startIcon={<ArrowUpwardRoundedIcon />} onClick={() => setDialog("income")} sx={{ textTransform: "none", fontWeight: 900 }}>Ingreso</Button><Button size="small" color="inherit" variant="outlined" startIcon={<ArrowDownwardRoundedIcon />} onClick={() => setDialog("expense")} sx={{ textTransform: "none", fontWeight: 900 }}>Gasto</Button></Stack>
    </Stack>

    <Box sx={{ ...surfaceSx, mb: .7, overflow: "hidden" }}><Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 42, "& .MuiTab-root": { minHeight: 42, textTransform: "none", fontWeight: 900, fontSize: ".72rem", px: 1.2 } }}>{NAV.map((item) => <Tab key={item.value} value={item.value} icon={item.icon} iconPosition="start" label={item.label} />)}</Tabs></Box>
    {error ? <Alert severity="error" onClose={() => setError("")} sx={{ mb: .7 }}>{error}</Alert> : null}

    {loadingBase && tab !== "movements" ? <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={28} /></Stack> : null}

    {!loadingBase && tab === "overview" ? <Stack spacing={.7}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" }, gap: .65 }}>
        <StatCard label="CUENTAS ACTIVAS" value={accounts.filter((row) => !row.archived).length} />
        <StatCard label="MOVIMIENTOS EN VISTA" value={pagination.total || transactions.length} helper="Usá Movimientos para filtrar el libro." />
        <StatCard label="CATEGORÍAS" value={categories.filter((row) => !row.archived).length + detectedCategories.length} helper={detectedCategories.length ? `${detectedCategories.length} detectadas sin normalizar` : "Catálogo normalizado"} />
        <StatCard label="PROYECTOS RELACIONABLES" value={projects.length} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "310px minmax(0,1fr)" }, gap: .7 }}>
        <Box sx={{ ...surfaceSx, p: .85 }}><Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950, mb: .55 }}>CUENTAS</Typography><Stack spacing={.35}>{accounts.filter((row) => !row.archived).map((row) => <Box key={idOf(row)} sx={{ px: .75, py: .65, borderRadius: 1.6, bgcolor: "action.hover" }}><Stack direction="row" justifyContent="space-between" spacing={1}><Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 900, fontSize: ".76rem" }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".58rem" }}>{row.type} · {row.currency}</Typography></Box><Typography sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>{money(row.balance, row.currency)}</Typography></Stack></Box>)}{!accounts.filter((row) => !row.archived).length ? <Typography sx={{ color: "text.secondary", fontSize: ".76rem", py: 1 }}>Creá tu primera cuenta.</Typography> : null}</Stack>{totalBalanceByCurrency.length > 1 ? <Stack direction="row" spacing={.4} useFlexGap flexWrap="wrap" sx={{ mt: .65 }}>{totalBalanceByCurrency.map((row) => <Chip key={row.currency} size="small" variant="outlined" label={`${row.currency}: ${money(row.total, row.currency)}`} />)}</Stack> : null}</Box>
        <Box sx={{ ...surfaceSx, p: .85 }}><Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: .55 }}><Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950 }}>ÚLTIMOS MOVIMIENTOS</Typography><Button size="small" onClick={() => setTab("movements")} sx={{ textTransform: "none", fontSize: ".65rem", fontWeight: 900 }}>Ver todos</Button></Stack><MovementRows rows={latest} accounts={accounts} onEdit={(row) => setEditingMovement(row)} /></Box>
      </Box>
    </Stack> : null}

    {tab === "movements" ? <Stack spacing={.65}>
      <Box sx={{ ...surfaceSx, p: .8 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={.55} alignItems={{ lg: "center" }}>
          <TextField size="small" fullWidth placeholder="Buscar descripción, categoría, área…" value={draftFilters.q} onChange={(e) => setDraftFilters({ ...draftFilters, q: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment> }} />
          <TextField size="small" select label="Tipo" value={draftFilters.type} onChange={(e) => setDraftFilters({ ...draftFilters, type: e.target.value })} sx={{ minWidth: 125 }}><MenuItem value="">Todos</MenuItem>{Object.entries(TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
          <TextField size="small" select label="Categoría" value={draftFilters.category} onChange={(e) => setDraftFilters({ ...draftFilters, category: e.target.value })} sx={{ minWidth: 150 }}><MenuItem value="">Todas</MenuItem>{categoryOptions.filter((row) => !row.archived).map((row) => <MenuItem key={`${row.kind}:${idOf(row)}`} value={row.name}>{row.name}</MenuItem>)}</TextField>
          <TextField size="small" select label="Cuenta" value={draftFilters.accountId} onChange={(e) => setDraftFilters({ ...draftFilters, accountId: e.target.value })} sx={{ minWidth: 145 }}><MenuItem value="">Todas</MenuItem>{accounts.filter((row) => !row.archived).map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}</MenuItem>)}</TextField>
          <TextField size="small" select label="Origen" value={draftFilters.sourceKind} onChange={(e) => setDraftFilters({ ...draftFilters, sourceKind: e.target.value })} sx={{ minWidth: 140 }}><MenuItem value="">Todos</MenuItem>{Object.entries(SOURCE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
          <TextField size="small" select label="Orden" value={draftFilters.sortBy} onChange={(e) => setDraftFilters({ ...draftFilters, sortBy: e.target.value })} sx={{ minWidth: 190 }}><MenuItem value="createdAt">Últimos registrados</MenuItem><MenuItem value="occurredAt">Fecha del movimiento</MenuItem></TextField>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={.55} alignItems={{ sm: "center" }} sx={{ mt: .55 }}>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={draftFilters.from} onChange={(e) => setDraftFilters({ ...draftFilters, from: e.target.value })} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={draftFilters.to} onChange={(e) => setDraftFilters({ ...draftFilters, to: e.target.value })} />
          <Button variant="contained" startIcon={<FilterAltRoundedIcon />} onClick={applyFilters} disabled={loadingMovements} sx={{ textTransform: "none", fontWeight: 900 }}>Aplicar filtros</Button>
          <Button onClick={clearFilters} disabled={loadingMovements} sx={{ textTransform: "none", fontWeight: 850 }}>Limpiar</Button>
          <Typography sx={{ ml: { sm: "auto" }, color: "text.secondary", fontSize: ".65rem" }}>{pagination.total || 0} movimientos · {filters.sortBy === "createdAt" ? "recientemente registrados" : "por fecha del movimiento"}</Typography>
        </Stack>
      </Box>
      {loadingMovements ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={28} /></Stack> : <MovementRows rows={transactions} accounts={accounts} onEdit={(row) => setEditingMovement(row)} />}
      {pagination.pages > 1 ? <Stack direction="row" justifyContent="center" alignItems="center" spacing={.7} sx={{ py: .35 }}><Button size="small" variant="outlined" disabled={loadingMovements || pagination.page <= 1} onClick={() => loadMovements(pagination.page - 1, filters)}>Anterior</Button><Typography sx={{ fontSize: ".68rem", fontWeight: 900 }}>Página {pagination.page} de {pagination.pages}</Typography><Button size="small" variant="outlined" disabled={loadingMovements || !pagination.hasMore} onClick={() => loadMovements(pagination.page + 1, filters)}>Siguiente</Button></Stack> : null}
    </Stack> : null}

    {tab === "economy" ? <EconomyView economy={economy} loading={economyLoading} accounts={accounts} categories={categoryOptions} clients={clients} services={services} onReload={loadEconomy} /> : null}

    {tab === "stats" ? <StatsView stats={stats} economy={economy} loading={statsLoading} accounts={accounts} categories={categoryOptions} filter={statsFilter} setFilter={setStatsFilter} onReload={loadStats} /> : null}

    {tab === "categories" ? <Stack spacing={.7}>
      <Alert severity="info">Renombrar una categoría actualiza en bloque todos los movimientos que usan ese nombre dentro del tipo correspondiente. No toca montos, cuentas ni fechas.</Alert>
      <Box sx={{ ...surfaceSx, p: .85 }}><Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: .6 }}><Box><Typography sx={{ fontWeight: 1000, fontSize: ".82rem" }}>Categorías administradas</Typography><Typography sx={{ color: "text.secondary", fontSize: ".62rem" }}>Catálogo explícito para formularios, filtros y estadísticas.</Typography></Box></Stack><Stack spacing={.35}>{categories.filter((row) => !row.archived).map((row) => <Box key={idOf(row)} sx={{ display: "flex", alignItems: "center", gap: .7, px: .75, py: .65, border: "1px solid", borderColor: "divider", borderRadius: 1.6 }}><Box sx={{ flex: 1, minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 950, fontSize: ".75rem" }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".6rem" }}>{KIND_LABELS[row.kind] || row.kind}</Typography></Box><Button size="small" startIcon={<EditRoundedIcon />} onClick={() => setEditingCategory(row)} sx={{ textTransform: "none", fontWeight: 900 }}>Renombrar grupo</Button></Box>)}{!categories.filter((row) => !row.archived).length ? <Typography sx={{ color: "text.secondary", fontSize: ".72rem", py: 1 }}>Todavía no hay categorías administradas.</Typography> : null}</Stack></Box>
      {detectedCategories.length ? <Box sx={{ ...surfaceSx, p: .85 }}><Typography sx={{ fontWeight: 1000, fontSize: ".82rem" }}>Detectadas en movimientos</Typography><Typography sx={{ color: "text.secondary", fontSize: ".62rem", mb: .6 }}>Son nombres históricos todavía no incorporados formalmente al catálogo.</Typography><Stack spacing={.35}>{detectedCategories.map((row) => <Box key={`${row.kind}:${row.name}`} sx={{ display: "flex", alignItems: "center", gap: .7, px: .75, py: .65, borderRadius: 1.6, bgcolor: "action.hover" }}><Box sx={{ flex: 1, minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 950, fontSize: ".75rem" }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".6rem" }}>{KIND_LABELS[row.kind] || row.kind} · {row.uses || 0} movimientos</Typography></Box><Button size="small" onClick={() => setEditingCategory(row)} sx={{ textTransform: "none", fontWeight: 900 }}>Normalizar / renombrar</Button></Box>)}</Stack></Box> : null}
    </Stack> : null}

    <MovementDialog open={dialog === "income" || dialog === "expense"} type={dialog === "income" ? "income" : "expense"} accounts={accounts} categories={categoryOptions} clients={clients} projects={projects} onClose={() => setDialog("")} onSaved={refreshAll} />
    <AccountDialog open={dialog === "account"} onClose={() => setDialog("")} onSaved={refreshAll} />
    <MovementEditDialog row={editingMovement} open={Boolean(editingMovement)} accounts={accounts} categories={categoryOptions} clients={clients} projects={projects} onClose={() => setEditingMovement(null)} onSaved={refreshAll} />
    <CategoryRenameDialog category={editingCategory} open={Boolean(editingCategory)} onClose={() => setEditingCategory(null)} onSaved={refreshAll} />
  </Box>;
}
